import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Eye, Pencil, Trash2, Building2 } from 'lucide-react'
import { companiesApi, type Company } from '../../api/endpoints'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Textarea } from '../../components/ui/Input'
import PhoneInput from '../../components/ui/PhoneInput'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import { useConfirm } from '../../components/ui/ConfirmDialog'
import { StageBadge } from '../../components/ui/Badge'

export default function CompaniesPage() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const limit = 25
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Company | null>(null)
  const [detailId, setDetailId] = useState<number | null>(null)

  const params: Record<string, string> = { limit: String(limit), offset: String(offset) }
  if (search) params.search = search

  const { data, isLoading } = useQuery({
    queryKey: ['companies', params],
    queryFn: () => companiesApi.list(params),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => companiesApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  })

  const [confirmNode, confirm] = useConfirm()

  const companies = data?.companies || []
  const total = data?.total || 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <Button onClick={() => { setEditing(null); setModalOpen(true) }}>
          <Plus size={16} /> Add Company
        </Button>
      </div>

      <div className="relative max-w-xs mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search companies..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0) }}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {confirmNode}

      {isLoading ? (
        <LoadingState message="Loading companies..." />
      ) : companies.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No companies yet"
          description="Add a company to group leads under a common entity"
          action={<Button onClick={() => { setEditing(null); setModalOpen(true) }}><Plus size={16} /> Add Company</Button>}
        />
      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-2">
            {companies.map((c) => (
              <div key={c.id} onClick={() => setDetailId(c.id)} className="bg-white rounded-xl border border-gray-200 p-4 active:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                      {c.industry && <span>{c.industry}</span>}
                      {c.city && <span>{c.city}</span>}
                    </div>
                    {c.website && <p className="text-xs text-gray-400 mt-0.5 truncate">{c.website}</p>}
                  </div>
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-full flex-shrink-0 ml-3">
                    {c.lead_count}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Industry</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">City</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Leads</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setDetailId(c.id)}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{c.name}</p>
                      {c.website && <p className="text-xs text-gray-400">{c.website}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.industry || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.city || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center w-7 h-7 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
                        {c.lead_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{c.phone || '—'}</td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setDetailId(c.id)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"><Eye size={15} /></button>
                        <button onClick={() => { setEditing(c); setModalOpen(true) }} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"><Pencil size={15} /></button>
                        <button onClick={async () => { if (await confirm({ message: 'Are you sure you want to delete this company?', title: 'Delete Company', confirmLabel: 'Delete', confirmVariant: 'danger' })) deleteMutation.mutate(c.id) }} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination total={total} limit={limit} offset={offset} onChange={setOffset} />
        </>
      )}

      <CompanyFormModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null) }} company={editing} />

      {detailId && <CompanyDetailModal companyId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  )
}

function CompanyFormModal({ open, onClose, company }: { open: boolean; onClose: () => void; company: Company | null }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: company?.name || '',
    website: company?.website || '',
    industry: company?.industry || '',
    business_type: company?.business_type || '',
    size: company?.size || '',
    city: company?.city || '',
    state: company?.state || '',
    phone: company?.phone || '',
    notes: company?.notes || '',
    address: company?.address || '',
  })

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => company ? companiesApi.update(company.id, data) : companiesApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['companies'] }); onClose() },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data = { ...form }
    Object.keys(data).forEach(k => { if (data[k as keyof typeof data] === '') delete data[k as keyof typeof data] })
    mutation.mutate(data)
  }

  if (open && company && form.name !== company.name) {
    setForm({
      name: company.name || '', website: company.website || '', industry: company.industry || '',
      business_type: company.business_type || '', size: company.size || '', city: company.city || '',
      state: company.state || '', phone: company.phone || '', notes: company.notes || '', address: company.address || '',
    })
  }

  return (
    <Modal open={open} onClose={onClose} title={company ? 'Edit Company' : 'Add Company'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {mutation.error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{(mutation.error as Error).message}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Company Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
          <Input label="Website" value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} />
          <Input label="Industry" value={form.industry} onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))} />
          <Input label="Business Type" value={form.business_type} onChange={(e) => setForm(f => ({ ...f, business_type: e.target.value }))} />
          <Input label="Size" value={form.size} onChange={(e) => setForm(f => ({ ...f, size: e.target.value }))} placeholder="e.g. 11-50" />
          <PhoneInput label="Phone" value={form.phone} onChange={(val) => setForm(f => ({ ...f, phone: val }))} />
          <Input label="City" value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} />
          <Input label="State" value={form.state} onChange={(e) => setForm(f => ({ ...f, state: e.target.value }))} />
        </div>
        <Textarea label="Address" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} />
        <Textarea label="Notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>{company ? 'Save Changes' : 'Create Company'}</Button>
        </div>
      </form>
    </Modal>
  )
}

function CompanyDetailModal({ companyId, onClose }: { companyId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companiesApi.get(companyId),
  })

  const company = data?.data

  return (
    <Modal open={true} onClose={onClose} title={company?.name || 'Company Detail'} size="xl">
      {isLoading ? (
        <div className="flex items-center justify-center h-32 text-gray-400">Loading...</div>
      ) : !company ? (
        <p className="text-gray-500">Company not found</p>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><p className="text-xs text-gray-500">Industry</p><p className="text-sm font-medium">{company.industry || '—'}</p></div>
            <div><p className="text-xs text-gray-500">City</p><p className="text-sm font-medium">{company.city || '—'}</p></div>
            <div><p className="text-xs text-gray-500">Size</p><p className="text-sm font-medium">{company.size || '—'}</p></div>
            <div><p className="text-xs text-gray-500">Phone</p><p className="text-sm font-medium">{company.phone || '—'}</p></div>
          </div>

          {company.avg_score != null && (
            <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-6">
              <div>
                <p className="text-xs text-gray-500">Avg Lead Score</p>
                <p className="text-2xl font-bold">{Math.round(company.avg_score)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Leads</p>
                <p className="text-2xl font-bold">{company.lead_count}</p>
              </div>
              {Object.entries(company.pipeline_summary || {}).map(([stage, count]) => (
                <div key={stage}>
                  <p className="text-xs text-gray-500 capitalize">{stage.replace('_', ' ')}</p>
                  <p className="text-lg font-semibold">{count as number}</p>
                </div>
              ))}
            </div>
          )}

          {company.leads?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Associated Leads ({company.leads.length})</h3>
              <div className="space-y-1.5">
                {company.leads.map((lead) => (
                  <div key={lead.id} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-gray-900 flex-1">{lead.name}</span>
                    <span className="text-sm text-gray-500">{lead.email || ''}</span>
                    <StageBadge stage={lead.stage} />
                    <span className="text-xs text-gray-500">{lead.score != null ? `${Math.round(lead.score as number)} pts` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
